import type { StepParticipant } from "@lib/selectionSteps";
import { Minus, Plus } from "lucide-react";

export type { StepParticipant };

const PARTICIPANT_ROLES = [
  { key: "recruiter", label: "Recruiter" },
  { key: "hiring_manager", label: "Hiring Manager" },
  { key: "interviewer", label: "Interviewer" },
  { key: "panelist", label: "Panelist" },
  { key: "candidate", label: "Candidate" },
  { key: "other", label: "Other" },
] as const;

export interface TeamMember {
  id: string;
  name: string;
}

interface StepParticipantsEditorProps {
  value: StepParticipant[];
  onChange: (next: StepParticipant[]) => void;
  teamMembers?: TeamMember[];
  idPrefix: string;
}

const emptyRow = (): StepParticipant => ({
  role: "interviewer",
  name: null,
  organisationMembershipId: null,
});

export function StepParticipantsEditor({
  value,
  onChange,
  teamMembers = [],
  idPrefix,
}: StepParticipantsEditorProps) {
  const updateRow = (index: number, patch: Partial<StepParticipant>) =>
    onChange(value.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const addRow = () => onChange([...value, emptyRow()]);

  const removeRow = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <div className="space-y-2">
      {value.length === 0 && <p className="text-xs text-text-muted">No participants yet</p>}
      {value.map((row, i) => {
        const roleId = `${idPrefix}-participant-${i}-role`;
        const nameId = `${idPrefix}-participant-${i}-name`;
        const memberId = `${idPrefix}-participant-${i}-member`;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: participants don't reorder; rows have no stable id until saved
          <div key={`${idPrefix}-${i}`} className="flex flex-wrap items-center gap-2">
            <select
              id={roleId}
              aria-label="Participant role"
              value={row.role}
              onChange={(e) => updateRow(i, { role: e.target.value })}
              className="rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {PARTICIPANT_ROLES.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
            <input
              id={nameId}
              type="text"
              value={row.name ?? ""}
              onChange={(e) =>
                updateRow(i, { name: e.target.value.trim() === "" ? null : e.target.value })
              }
              aria-label="Participant name"
              placeholder="Name (optional)"
              className="flex-1 min-w-[160px] rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
            {teamMembers.length > 0 && (
              <select
                id={memberId}
                aria-label="Team member"
                value={row.organisationMembershipId ?? ""}
                onChange={(e) =>
                  updateRow(i, {
                    organisationMembershipId: e.target.value === "" ? null : e.target.value,
                  })
                }
                className="rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">— Team member (optional) —</option>
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => removeRow(i)}
              aria-label="Remove participant"
              className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
            >
              <Minus size={14} />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"
      >
        <Plus size={12} /> Add participant
      </button>
    </div>
  );
}
